import { getBalanceSheet, getCashFlow } from "../../apiClient";
import { formatDateForAPI } from "../dateUtils";
import { groupCashFlowByCategory, sumBalances } from "../collectionUtils";
import { formatReportApiError, isRequestCancelled } from "../errorUtils";
import {
  calculateCash,
  calculateCashEquivalents,
  calculateCurrentRatio,
  calculateDaysWorkingCapital,
  calculateDefensiveInterval,
  calculateFreeCashFlow,
  calculateNetCashFlow,
  calculateOperatingCashFlow,
  calculateQuickAssets,
  calculateQuickRatio,
  formatCashFlowSection,
  getCurrentAssetsDetails,
  getCurrentLiabilitiesDetails,
} from "../liquidityMetrics";
import { formatBalanceSheetData } from "../formatters/balanceSheet";
import { getMockReportData } from "../mockData";
import { findRowByGroupDeep, getNodePrimaryValue } from "../ai/summaryUtils";
import { formatCashFlowData } from "./cashFlow";

const collectLeafAccounts = (node, category, bucket) => {
  const children = Array.isArray(node?.children) ? node.children : [];
  if (children.length === 0) {
    bucket.push({
      name: node?.label || node?.id || "Unknown",
      balance: getNodePrimaryValue(node),
      category,
    });
    return;
  }
  children.forEach((child) => collectLeafAccounts(child, category, bucket));
};

// Wafeq returns the balance sheet as a nested rows tree, while getCurrentTotals and
// liquidityMetrics expect flat {assets, liabilities} arrays carrying a category field.
// Only leaf accounts are collected — parent nodes duplicate their children's totals.
const toFlatBalanceView = (balanceData = {}) => {
  if (!Array.isArray(balanceData?.rows)) return balanceData;

  const view = { assets: [], liabilities: [], equity: [] };
  [
    ["CURRENT_ASSET", "assets", "current"],
    ["NON_CURRENT_ASSET", "assets", "non_current"],
    ["FIXED_ASSET", "assets", "non_current"],
    ["CURRENT_LIABILITY", "liabilities", "current"],
    ["NON_CURRENT_LIABILITY", "liabilities", "non_current"],
  ].forEach(([groupName, target, category]) => {
    const groupRow = findRowByGroupDeep(balanceData.rows, groupName);
    (groupRow?.children || []).forEach((child) => collectLeafAccounts(child, category, view[target]));
  });

  return view;
};

const getCurrentTotals = (balanceData) => ({
  currentAssets: sumBalances(
    balanceData.assets?.filter(
      (item) =>
        item.category === "current" ||
        item.type === "current" ||
        item.name?.includes("متداول") ||
        item.account_type === "current_asset"
    ) || []
  ),
  currentLiabilities: sumBalances(
    balanceData.liabilities?.filter(
      (item) =>
        item.category === "current" ||
        item.type === "current" ||
        item.name?.includes("متداول") ||
        item.account_type === "current_liability"
    ) || []
  ),
});

const formatWorkingCapitalData = (balanceData, cashFlowData) => {
  const balanceView = toFlatBalanceView(balanceData);
  const { currentAssets, currentLiabilities } = getCurrentTotals(balanceView);
  const workingCapital = currentAssets - currentLiabilities;
  const operatingCashFlow = calculateOperatingCashFlow(cashFlowData);

  return {
    summary: {
      currentAssets,
      currentLiabilities,
      workingCapital,
      operatingCashFlow,
      freeCashFlow: calculateFreeCashFlow(cashFlowData),
    },
    metrics: {
      workingCapitalRatio: currentLiabilities > 0 ? currentAssets / currentLiabilities : 0,
      currentRatio: calculateCurrentRatio(balanceView),
      quickRatio: calculateQuickRatio(balanceView),
      cashFlowToWorkingCapital: workingCapital > 0 ? operatingCashFlow / workingCapital : 0,
      operatingCashFlowRatio: currentLiabilities > 0 ? operatingCashFlow / currentLiabilities : 0,
      daysWorkingCapital: calculateDaysWorkingCapital(balanceView, cashFlowData),
    },
    components: {
      currentAssets: getCurrentAssetsDetails(balanceView),
      currentLiabilities: getCurrentLiabilitiesDetails(balanceView),
      cashFlow: {
        operating: groupCashFlowByCategory(cashFlowData.operating || []),
        investing: groupCashFlowByCategory(cashFlowData.investing || []),
        financing: groupCashFlowByCategory(cashFlowData.financing || []),
      },
    },
    combinedData: {
      balanceSheet: formatBalanceSheetData(balanceData),
      cashFlow: formatCashFlowData(cashFlowData),
    },
  };
};

const formatLiquidityData = (balanceData, cashFlowData) => {
  const balanceView = toFlatBalanceView(balanceData);
  const quickAssets = calculateQuickAssets(balanceView);
  const { currentAssets, currentLiabilities } = getCurrentTotals(balanceView);
  const operatingCashFlow = calculateOperatingCashFlow(cashFlowData);

  return {
    summary: {
      quickAssets,
      currentAssets,
      currentLiabilities,
      operatingCashFlow,
      netCashFlow: calculateNetCashFlow(cashFlowData),
    },
    liquidityRatios: {
      currentRatio: currentLiabilities > 0 ? currentAssets / currentLiabilities : 0,
      quickRatio: currentLiabilities > 0 ? quickAssets / currentLiabilities : 0,
      cashRatio: currentLiabilities > 0 ? calculateCash(balanceView) / currentLiabilities : 0,
      operatingCashFlowRatio: currentLiabilities > 0 ? operatingCashFlow / currentLiabilities : 0,
      netWorkingCapital: currentAssets - currentLiabilities,
      defensiveInterval: calculateDefensiveInterval(balanceView, cashFlowData),
    },
    cashPosition: {
      cash: calculateCash(balanceView),
      cashEquivalents: calculateCashEquivalents(balanceView),
      totalLiquidAssets: quickAssets,
      cashFlowFromOperations: operatingCashFlow,
    },
    cashFlowAnalysis: {
      operating: formatCashFlowSection(cashFlowData.operating || []),
      investing: formatCashFlowSection(cashFlowData.investing || []),
      financing: formatCashFlowSection(cashFlowData.financing || []),
    },
    combinedData: {
      balanceSheet: formatBalanceSheetData(balanceData),
      cashFlow: formatCashFlowData(cashFlowData),
    },
  };
};

export const handleWorkingCapitalLiquidity = async (
  filters,
  { balanceDate, rangeEndDate, rangeStartDate },
  reportType,
  language = "ar",
  options = {}
) => {
  try {
    const [balanceResponse, cashFlowResponse] = await Promise.all([
      getBalanceSheet({
        date: balanceDate,
        currency: filters.currency,
        period_count: filters.period_count || 0,
      }, { signal: options.signal }),
      getCashFlow({
        currency: filters.currency,
        date_after: formatDateForAPI(rangeStartDate),
        date_before: formatDateForAPI(rangeEndDate),
        compare_period_unit: filters.compare_period_unit || "month",
        ...(filters.cash_flow_type && { cash_flow_type: filters.cash_flow_type }),
      }, { signal: options.signal }),
    ]);

    return {
      success: true,
      reportType,
      isRealData: true,
      originalData: {
        balanceSheet: balanceResponse.data,
        cashFlow: cashFlowResponse.data,
      },
      data:
        reportType === "liquidity"
          ? formatLiquidityData(balanceResponse.data, cashFlowResponse.data)
          : formatWorkingCapitalData(balanceResponse.data, cashFlowResponse.data),
    };
  } catch (error) {
    if (isRequestCancelled(error)) throw error;
    console.error(`${reportType} API Error:`, error);
    return {
      success: false,
      reportType,
      isRealData: false,
      data: getMockReportData(reportType, filters),
      error: formatReportApiError(error, language),
    };
  }
};
